// eslint-disable-next-line no-redeclare
/* global document, window */
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import zipWith from 'lodash/zipWith';
import debounce from 'lodash/debounce';
const magPlus = `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 48.92 47.58'><g data-name='Layer 2'><g data-name='Layer 1'><path d='M48.6,45.85,32.28,30.5a18.56,18.56,0,1,0-1.4,1.43L47.23,47.31a1,1,0,1,0,1.37-1.46ZM2,18.42A16.42,16.42,0,1,1,18.42,34.83,16.44,16.44,0,0,1,2,18.42Z'/><path d='M27.26,17.92H9a.89.89,0,1,0,0,1.79H27.26a.89.89,0,1,0,0-1.79Z'/><path d='M19,27.82V9.59a.89.89,0,0,0-1.79,0V27.82a.89.89,0,1,0,1.79,0Z'/></g></g></svg>")`;
const magMinus = `url("data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' width='28' height='28' viewBox='0 0 48.92 47.58'><title>zoom-out</title><g data-name='Layer 2'><g data-name='Layer 1'><path d='M48.6,45.85,32.28,30.5a18.56,18.56,0,1,0-1.4,1.43L47.23,47.31a1,1,0,1,0,1.37-1.46ZM2,18.42A16.42,16.42,0,1,1,18.42,34.83,16.44,16.44,0,0,1,2,18.42Z'/><path d='M27.26,17.92H9a.89.89,0,1,0,0,1.79H27.26a.89.89,0,1,0,0-1.79Z'/></g></g></svg>")`;

const isEqual = (arr1, arr2) => {
  for (let i = 0; i < arr1.length; i++) {
    if (arr1[i] !== arr2[i]) {
      return false;
    }
  }
  return true;
};

const styles = {
  wrapper: {
    position: 'relative',
    zIndex: 1,
  },

  image: {
    position: 'relative',
    transitionProperty: 'z-index',
    width: '100%',
    height: '100%',
  },

  zoomContainer: {
    position: 'absolute',
    top: 0,
  },

  innerMap: {
    position: 'absolute',
    top: 10,
    left: 10,
    pointerEvents: 'none',
  },

  innerMapImage: {
    width: '101%',
    height: 'auto',
  },

  innerMapHighlightBox: {
    position: 'absolute',
    backgroundColor: 'rgba(140, 137, 131, 0.6)',
  },

  zoomedImage: {
    position: 'absolute',
    top: 0,
    backgroundColor: 'transparent',
    backgroundRepeat: 'no-repeat',
  },
};

const isInBounds = (coordinates, upperBounds) => {
  let flag = true;
  coordinates.forEach((marker, i) => {
    if (marker <= 0 || marker >= upperBounds[i]) {
      flag = false;
    }
  });
  return flag;
};

const nearestInBoundsPosition = (loc, bounds) => loc.map((p, i) => Math.min(p, bounds[i]));

class ZoomableImage extends Component {
  /**
   * Sets initial size of images, and zoom level into state
   * @param {*} props
   */
  constructor(props) {
    super(props);

    this.state = {
      zoomed: 0,
      zoomLevel: props.baseImage.width / props.largeImage.width,
      defaultZoomLevel: 1.2,
      viewWindowPosition: [0, 0],
      dragFocalPoint: [0, 0],
      lastClickToZoomPosition: [-1, -1],
      isCalculatingZoom: false,
      isDragging: false,
      calcMovementFromDrag: false,
      pageClickHandlersAreAdded: false,
      transitionZoomBackground: true,
      isStartingZoom: false,
      isEndingZoom: false,
      baseImage: {
        ...this.props.baseImage,
        width: this.props.baseImage.width,
        height: this.props.baseImage.height,
      },
    };
    this.calcBackgroundOffsetFromKey = this.calcBackgroundOffsetFromKey.bind(this);
  }
  updateZoomWrapperSize = () => {
    if (this.zoomContainer !== null) {
      this.zoomContainer.style.width = this.zoomContainer.offsetWidth + 'px';
      this.zoomContainer.style.height = this.zoomContainer.offsetHeight + 'px';
      this.zoomImg.style.width = this.zoomContainer.offsetWidth + 'px';
      this.zoomImg.style.height = this.zoomContainer.offsetHeight + 'px';
      this.setState({
        baseImage: {
          ...this.props.baseImage,
          width: parseInt(this.zoomContainer.style.width),
          height: parseInt(this.zoomContainer.style.height),
        },
      });
    }
  };
  /**
   * This is all a workaround to handle chrome disabling e.preventDefault()
   * unless you specify to enable it. Unfortunately this cannot be done through
   * React synthetic events
   */
  componentDidMount() {
    this.updateWrapperSize = _.debounce(this.updateZoomWrapperSize.bind(this), 500);
    window.addEventListener('load', this.updateWrapperSize);
    this.zoomWrapperSize = _.debounce(this.updateZoomWrapperSize.bind(this), 500);
    window.addEventListener('resize', this.zoomWrapperSize);
    this.bodyClickHandlers = [
      ['click', this.endZoom],
      ['touchstart', this.checkForTap],
      ['touchend', this.onTouchEnd],
    ];
    let passiveSupported = false;
    try {
      const options = Object.defineProperty({}, 'passive', {
        get: () => {
          passiveSupported = true;
          return undefined;
        },
      });
      document.querySelector('body').addEventListener('test', null, options);
    } catch (err) {
      passiveSupported = false;
    }
    const passive = passiveSupported ? { passive: false } : false;
    // if (this.zoomContainer) {
    //     this.zoomContainer.addEventListener('touchmove', this.calcBackgroundOffsetFromDrag, passive);
    //     this.zoomContainer.addEventListener('touchstart', this.onZoomWindowTouchStart, passive);
    //     this.zoomContainer.addEventListener('touchend', this.onTouchEnd, passive);
    //     // Initialize Hammer
    //     this.mc = new Manager(this.zoomContainer);
    //     this.mc.add(new Pinch());
    //     this.mc.on('pinchin', this.handleZoom);
    //     this.mc.on('pinchout', this.handleZoom);
    // }
  }

  /**
   * Make sure we don't leave any garbage behind
   */
  componentWillUnmount() {
    if (this.updateWrapperSize) {
      window.removeEventListener('load', this.updateWrapperSize);
      this.updateWrapperSize.cancel();
    }
    if (this.zoomWrapperSize) {
      window.removeEventListener('resize', this.zoomWrapperSize);
      this.zoomWrapperSize.cancel();
    }
    this.removeBodyClickHandlers();
  }

  startZoom = (initialFocalPoint, isTouchDevice) => {
    if (this.zoomImg) {
      this.zoomImg.style.setProperty('visibility', 'visible');
    }
    if (isTouchDevice) this.setState({ calcMovementFromDrag: true });
    if (!isEqual(this.state.viewWindowPosition, initialFocalPoint)) {
      const {
        baseImage: { width },
        largeImage,
      } = this.props;
      const largeWidth = largeImage.width;
      const largeHeight = largeImage.height;
      const largeImageSize = [largeWidth, largeHeight];

      let startingFocus = isTouchDevice ? initialFocalPoint.map((p) => p * (largeWidth / width)) : initialFocalPoint;

      if (isTouchDevice && !isInBounds(startingFocus, largeImageSize)) {
        startingFocus = nearestInBoundsPosition(startingFocus, largeImageSize);
      }
      this.setState({
        viewWindowPosition: startingFocus,
        lastClickToZoomPosition: initialFocalPoint,
        calcMovementFromDrag: isTouchDevice,
      });
    }
    this.setState({
      zoomed: 1,
      zoomLevel: this.state.defaultZoomLevel,
      pageClickHandlersAreAdded: true,
      isStartingZoom: true,
    });

    // Turning off transitions so there is no delay between mousemove and the background
    // image repositioning
    setTimeout(() => {
      this.addBodyClickHandlers();
      this.setState({
        transitionZoomBackground: false,
        isStartingZoom: false,
      });
    }, this.props.zoomTransitionTime);
  };

  endZoom = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (this.zoomImg) {
      this.zoomImg.style.setProperty('visibility', 'hidden');
    }
    const zoomLevel = this.props.baseImage.width / this.props.largeImage.width;
    // Transitioning out of zoom view
    this.removeBodyClickHandlers();
    this.setState({
      zoomed: 0,
      zoomLevel,
      pageClickHandlersAreAdded: false,
      transitionZoomBackground: true,
      isEndingZoom: true,
    });
    setTimeout(() => {
      this.setState({ isEndingZoom: false });
    }, this.props.zoomTransitionTime);
  };

  onZoomWindowTouchStart = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.targetTouches) {
      this.setState({
        isDragging: true,
        previousDragPosition: [e.targetTouches[0].clientX, e.targetTouches[0].clientY],
      });
    }
    this.checkForTap();
  };

  onTouchEnd = (e) => {
    // If this was a tap, not a drag, close the zoom view
    if (this.state.listeningForSingleClick) {
      this.endZoom(e);
    } else {
      e.stopPropagation();
      e.preventDefault();
    }
    this.setState({ isDragging: false });
  };

  /**
   * Close zoom view following a click or tap anywhere on the page
   */
  addBodyClickHandlers = () => {
    if (document && document.querySelector) {
      if (!this.bodyElement) this.bodyElement = document.querySelector('body');
      this.bodyClickHandlers.forEach((handler) => {
        this.bodyElement.addEventListener(...handler);
      });
    }
  };

  removeBodyClickHandlers = () => {
    try {
      if (document && document.querySelector) {
        this.bodyClickHandlers.forEach((handler) => {
          this.bodyElement.removeEventListener(...handler);
        });
      }
    } catch (e) {}
  };

  calcBackgroundOffsetFromMouse = (e) => {
    if (!this.state.isDragging && e.nativeEvent) {
      if (this.state.calcMovementFromDrag) this.setState({ calcMovementFromDrag: false });
      const { offsetX, offsetY } = e.nativeEvent;
      const viewWindowPosition = [offsetX, offsetY];

      window.requestAnimationFrame(() => {
        this.setState({ viewWindowPosition });
      });
    }
  };

  calcBackgroundOffsetFromKey = (event) => {
    console.log(`KEYCODE`);
    // if (!event.preventDefaultAction && !event.ctrl && !event.alt && !event.meta) {
    //     switch (event.keyCode) {
    //         case 38: //up arrow
    //             onStripScroll.call(this, { eventSource: this.tracker, position: null, scroll: 1, shift: null });
    //             return false;
    //         case 40: //down arrow
    //             onStripScroll.call(this, { eventSource: this.tracker, position: null, scroll: -1, shift: null });
    //             return false;
    //         case 37: //left arrow
    //             onStripScroll.call(this, { eventSource: this.tracker, position: null, scroll: -1, shift: null });
    //             return false;
    //         case 39: //right arrow
    //             onStripScroll.call(this, { eventSource: this.tracker, position: null, scroll: 1, shift: null });
    //             return false;
    //         default:
    //             console.log( 'navigator keycode %s', event.keyCode );
    //             return true;
    //     }
    // } else {
    //     return true;
    // }
  };

  calcBackgroundOffsetFromDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!this.state.calcMovementFromDrag) this.setState({ calcMovementFromDrag: true });

    const { zoomLevel, previousDragPosition, viewWindowPosition } = this.state;
    const { largeImage } = this.props;
    const largeImageSize = [largeImage.width, largeImage.height];

    if (e.targetTouches) {
      const currentDragPosition = [e.targetTouches[0].clientX, e.targetTouches[0].clientY];

      // Translate the view window focal point by the drag distance
      const dragScale = 3 / (zoomLevel * zoomLevel);
      const newFocus = zipWith(
        viewWindowPosition,
        currentDragPosition,
        previousDragPosition,
        (f, c, p) => f + (p - c) * dragScale
      );

      if (isInBounds(newFocus, largeImageSize)) {
        window.requestAnimationFrame(() => {
          this.setState({
            viewWindowPosition: newFocus,
            previousDragPosition: currentDragPosition,
          });
        });
      }
    }
  };

  checkForTap = () => {
    this.setState({ listeningForSingleClick: true });
    setTimeout(() => this.setState({ listeningForSingleClick: false }), 200);
  };

  pauseZoom = () => this.setState({ isCalculatingZoom: true });

  resumeZoom = () => this.setState({ isCalculatingZoom: false });

  handleZoom = (e) => {
    e.preventDefault();
    const { zoomLevel } = this.state;
    if (!this.state.isCalculatingZoom && zoomLevel <= 2 && zoomLevel >= 0) {
      if ((e.type === 'pinchout' || e.deltaY < 0) && zoomLevel < 2) {
        this.zoom(0.05);
      } else if ((e.type === 'pinchin' || e.deltaY > 0) && zoomLevel > 1) {
        this.zoom(-0.05);
      }
    }
  };

  zoom = (delta) => {
    this.pauseZoom();
    const zoomLevel = Math.round((this.state.zoomLevel + delta) * 100) / 100;
    this.setState({ zoomLevel }, this.resumeZoom);
  };

  /**
   * Renders.
   * @returns {*}
   */
  render() {
    const { baseImage, largeImage, thumbnailImage, displayMap, zoomTransitionTime, mapBorderColor, mapScaleFactor } =
      this.props;

    const {
      zoomed,
      viewWindowPosition,
      zoomLevel,
      calcMovementFromDrag,
      transitionZoomBackground,
      isStartingZoom,
      listeningForSingleClick,
      isEndingZoom,
      isTouch,
    } = this.state;
    // console.log("reg", baseImage);
    // console.log("state", this.state.baseImage);
    const { width, height } = this.state.baseImage;
    const largeWidth = largeImage.width;
    const largeHeight = largeImage.height;
    const baseToZoomRatio = width / (largeWidth * zoomLevel);
    const referencePosition = calcMovementFromDrag ? [largeWidth, largeHeight] : [width, height];
    const viewWindowPercent = viewWindowPosition.map(
      (offset, i) => Math.round((offset / referencePosition[i]) * 1000) / 10
    );
    return (
      <div
        data-name='wrapper'
        style={{ ...styles.wrapper, width, height, cursor: `${zoomed ? magMinus : magPlus}, pointer` }}
        onMouseDown={(e) => {
          var isRightMB;
          e = e || window.event;

          if ('which' in e)
            // Gecko (Firefox), WebKit (Safari/Chrome) & Opera
            isRightMB = e.which == 3;
          else if ('button' in e)
            // IE, Opera
            isRightMB = e.button == 2;
          if (!isRightMB && !zoomed && !isTouch) {
            this.startZoom([e.nativeEvent.offsetX, e.nativeEvent.offsetY], false);
          }
          this.calcBackgroundOffsetFromKey;
        }}
        onTouchStart={(e) => {
          if (!zoomed) {
            e.preventDefault();
            e.stopPropagation();
            this.setState({ listeningForSingleClick: true, isTouch: true });
            setTimeout(() => {
              this.setState({ listeningForSingleClick: false });
            }, 200);
            setTimeout(() => {
              this.setState({ isTouch: false });
            }, 2000);
          }
        }}
        onTouchEnd={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!zoomed && listeningForSingleClick && !isEndingZoom) {
            const { x, y } = e.target.getBoundingClientRect();
            this.startZoom([e.changedTouches[0].clientX - (x || 0), e.changedTouches[0].clientY - (y || 0)], true);
          }
        }}
        onMouseLeave={(e) => {
          if (this.state.zoomed == 1 && !isStartingZoom) this.endZoom(e);
        }}
      >
        <img
          id='main-product-image'
          src={baseImage.src}
          alt={baseImage.alt}
          width='570'
          height='661'
          style={{
            ...styles.image,
            zIndex: zoomed ? -1 : 1,
            transitionDelay: `${zoomTransitionTime}ms`,
            maxWidth: '100%',
            height: 'auto',
            width: 'auto',
          }}
        />
        <div
          data-name='zoom-wrapper'
          id='zoom-wrapper'
          ref={(domNode) => {
            this.zoomContainer = domNode;
          }}
          onMouseMove={this.calcBackgroundOffsetFromMouse}
          // onWheel={this.handleZoom}

          style={{ ...styles.zoomContainer, width, height, zIndex: zoomed * 100 }}
        >
          <div
            data-name='zoom-image'
            onClick={(e) => {
              if (zoomed && !isStartingZoom) this.endZoom(e);
            }}
            ref={(domNode) => {
              this.zoomImg = domNode;
            }}
            style={{
              width,
              height,
              zIndex: zoomed * 110,
              backgroundImage: zoomed
                ? `url(${largeImage.src.split('/mainProduct/').join('/zoom/')})`
                : `url(${largeImage.src})`,
              backgroundPosition: viewWindowPercent.map((p) => `${p}%`).join(' '),
              backgroundSize: `${Math.round(largeWidth * zoomLevel)}px ${Math.round(largeHeight * zoomLevel)}px`,
              visibility: 'hidden',
              ...styles.zoomedImage,
              transition: transitionZoomBackground ? `background-size ${zoomTransitionTime}ms ease-in-out` : 'none',
            }}
          />
          {this.state.zoomed ? (
            <div className='zoom-close'>
              <span>×</span>
            </div>
          ) : (
            ''
          )}
        </div>
      </div>
    );
  }
}

const imageShape = {
  alt: PropTypes.string.isRequired,
  src: PropTypes.string.isRequired,
};
const sizedImageShape = {
  ...imageShape,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
};

ZoomableImage.propTypes = {
  baseImage: PropTypes.shape(sizedImageShape).isRequired,
  displayMap: PropTypes.bool,
  largeImage: PropTypes.shape(sizedImageShape).isRequired,
  mapBorderColor: PropTypes.string,
  mapScaleFactor: PropTypes.number,
  thumbnailImage: PropTypes.shape(imageShape),
  zoomTransitionTime: PropTypes.number,
};

ZoomableImage.defaultProps = {
  zoomTransitionTime: 300,
  displayMap: true,
  mapBorderColor: 'grey',
  mapScaleFactor: 0.2,
  thumbnailImage: { alt: '', src: '' },
};

export default ZoomableImage;
